




























import Vue from "vue";
import Event from "@/components/arma/events/Event.vue";

import { PersistedEventInterface, SlotAvailability } from "@/types";
import PersistedSlotGroupInterface from "@/types/PersistedSlotGroupInterface";
import moment from "moment";

export default Vue.extend({
  components: {
    Event,
  },
  metaInfo(): object {
    const event = (this as any).event as (PersistedEventInterface & SlotAvailability) | undefined;
    const name = (event && event.name) || "Event";
    const description = (event && event.description)
      || "All information about the Arma 3 event is gathered on this page. Join by signing up with your Discord user.";
    const imageUrl = (this as any).eventImageUrl as string || "https://arma.npcs.games/logo.png";
    const robotsContent = (this as any).isArchivedEvent ? "noindex,follow" : "index,follow";
    return {
      title: `${name} – Newfound Pioneer Corps Arma 3`,
      meta: [
        { vmid: "description", name: "description", content: description },
        { vmid: "robots", name: "robots", content: robotsContent },
        { vmid: "og:title", property: "og:title", content: name },
        { vmid: "og:description", property: "og:description", content: description },
        { vmid: "og:image", property: "og:image", content: imageUrl },
        { vmid: "og:url", property: "og:url", content: (this as any).canonicalUrl },
        { vmid: "og:type", property: "og:type", content: "website" },
      ],
      link: [{ vmid: "canonical", rel: "canonical", href: (this as any).canonicalUrl }],
    };
  },
  data() {
    return {
      event: undefined as
        | (PersistedEventInterface & SlotAvailability)
        | undefined,
      eventImageUrl: "" as string,
      endpoint: "/arma/events/",
      contentWidth: 1200,
      attrs: {
        class: "mb-6",
        boilerplate: true,
        elevation: 2,
      },
    };
  },
  created() {
    this.getEventData();
  },
  computed: {
    canonicalUrl(): string {
      return "https://arma.npcs.games" + encodeURI(`/events/${this.$route.params.name}`);
    },
    isArchivedEvent(): boolean {
      if (this.event === undefined) {
        return false;
      }

      if (this.event.finished) {
        return true;
      }

      const startDate = this.startDate();
      if (!startDate) {
        return false;
      }

      return moment(startDate).isBefore(moment());
    },
    schemaEventStatus(): string {
      return this.isArchivedEvent
        ? "https://schema.org/EventCompleted"
        : "https://schema.org/EventScheduled";
    },
    eventUri(): string {
      return encodeURI(`/events/${this.$route.params.name}`);
    },
    jsonLd(): Record<string, any> {
      let startDate = this.startDate();
      if (!startDate) {
        startDate = "";
      }
      const date: string = startDate;
      return {
        "@context": "https://schema.org",
        "@type": "Event",
        "name": this.event!.name,
        "startDate": date,
        "eventStatus": this.schemaEventStatus,
        "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
        "location": {
          "@type": "VirtualLocation",
          "url": "https://arma.npcs.games" + encodeURI("/events/" + this.$route.params.name),
        },
        "image": this.eventImageUrl ? [this.eventImageUrl] : [],
        "description": this.event!.description,
        "organizer": {
          "@type": "Organization",
          "name": "Newfound Pioneer Corps",
          "url": "https://arma.npcs.games",
        },
        "url": "https://arma.npcs.games" + encodeURI("/events/" + this.$route.params.name),
        "isAccessibleForFree": true,
        "maximumAttendeeCapacity": this.event!.totalSlots,
        "remainingAttendeeCapacity": this.event!.openSlots,
      };
    },
  },
  watch: {
    "$route.params.name": "getEventData",
  },
  methods: {
    startDate(): string | undefined {
      if (this.event === undefined || !this.event.date) {
        return;
      }
      return moment
        .tz(this.event!.date.date.substring(0, 23), this.event!.date.timezone)
        .format();
    },
    async getEventData() {
      const response = await this.axios.get(this.endpoint, {
        params: {
          name: this.$route.params.name,
        },
      });

      if (response.data) {
        this.setEventSlotCounts(response.data[0]);
        if (this.event?.image) {
          try {
            const imgResponse = await this.axios.get("/arma/eventImages/" + this.event.id);
            this.eventImageUrl = imgResponse.data;
          } catch {
            this.eventImageUrl = "";
          }
        }
      } else {
        await this.$router.replace({ name: "404" });
      }
    },
    setSlotGroupSlotCounts(slotGroup: PersistedSlotGroupInterface) {
      const slotGroupWithAvailability =
        slotGroup as PersistedSlotGroupInterface & SlotAvailability;
      slotGroupWithAvailability.totalSlots =
        slotGroupWithAvailability.playerSlots.length
        + slotGroupWithAvailability.lotterySlots.length;
      slotGroupWithAvailability.openSlots = 0;

      slotGroupWithAvailability.playerSlots.forEach((slot) => {
        if (slot.discordUser === null) {
          slotGroupWithAvailability.openSlots++;
        }
      });

      slotGroupWithAvailability.openSlots += slotGroupWithAvailability.lotterySlots.length;

      return slotGroupWithAvailability;
    },
    setEventSlotCounts(event: PersistedEventInterface) {
      const eventWithAvailability = event as PersistedEventInterface &
        SlotAvailability;
      const slotGroupsWithAvailability: Array<
        PersistedSlotGroupInterface & SlotAvailability
      > = [];

      eventWithAvailability.slotGroups.forEach((slotGroup) => {
        slotGroupsWithAvailability.push(this.setSlotGroupSlotCounts(slotGroup));
      });

      eventWithAvailability.openSlots = 0;
      eventWithAvailability.totalSlots = 0;

      eventWithAvailability.slotGroups = slotGroupsWithAvailability;
      const eventSlotGroups = eventWithAvailability.slotGroups as Array<
        PersistedSlotGroupInterface & SlotAvailability
      >;

      eventSlotGroups.forEach((slotGroup) => {
        eventWithAvailability.openSlots += slotGroup.openSlots;
        eventWithAvailability.totalSlots += slotGroup.totalSlots;
      });

      this.event = eventWithAvailability;
    },
  },
  beforeRouteEnter(to, from, next) {
    localStorage.setItem("eventName", to.params.name);
    next();
  },
});
